import { CANCELLATION_POLICY_VERSION, cancellationPolicy } from "@/lib/cancellation-policy";
import { HoldCheckoutPanel } from "@/components/booking/HoldCheckoutPanel";
// Pantalla real de reserva sobre el entorno de PRUEBA de Bókun.
// Disponibilidad, precios e importe del pago vienen del servidor; el navegador
// sólo elige fecha, viajeros y datos de contacto.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { AlertCircle, Loader2, Minus, Plus } from "lucide-react";

import {
  getBokunTestBookingScreen,
  startBokunTestBooking,
} from "@/lib/bokun-test-booking.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BookingPhoneInput } from "@/components/booking/BookingPhoneInput";
import { bookingPhone, defaultPhoneCountry } from "@/lib/booking-phone";

export const Route = createFileRoute("/reservar-prueba/$activityId")({
  head: () => ({
    meta: [
      { title: "Reserva tu experiencia | Mostrando México" },
      {
        name: "description",
        content:
          "Elige fecha, horario y viajeros, y aparta tu lugar con pago seguro en Mostrando México.",
      },
      { property: "og:title", content: "Reserva tu experiencia | Mostrando México" },
      {
        property: "og:description",
        content: "Disponibilidad real, precios en pesos y confirmación inmediata.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReservarPruebaPage,
});

function money(amount: number, currency: string) {
  return `${currency} ${amount.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;
}

function ReservarPruebaPage() {
  const { activityId } = Route.useParams();
  const fetchScreen = useServerFn(getBokunTestBookingScreen);
  const start = useServerFn(startBokunTestBooking);
  const [hold, setHold] = useState<{ clientSecret: string; payDeadline: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["bokun-test-booking-screen", activityId],
    queryFn: () => fetchScreen({ data: { activityId } }),
    staleTime: 60_000,
  });

  const screen = data && "product" in data ? data : null;
  const loadError = data && "error" in data ? data.error : null;

  const [slotKey, setSlotKey] = useState<string | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [extras, setExtras] = useState<Record<string, number>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [pickupPlaceId, setPickupPlaceId] = useState<string>("");
  const [pickupNote, setPickupNote] = useState("");
  const [contact, setContact] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [phoneCountry, setPhoneCountry] = useState(() => defaultPhoneCountry("es"));
  const [error, setError] = useState<string | null>(null);

  const slots = screen?.slots ?? [];
  const slot = useMemo(
    () => slots.find((s) => s.key === slotKey) ?? slots[0] ?? null,
    [slots, slotKey],
  );

  const total = useMemo(() => {
    if (!slot) return 0;
    return slot.prices.reduce((sum, p) => sum + p.amount * (qty[p.pricingCategoryId] ?? 0), 0);
  }, [slot, qty]);
  const participants = useMemo(() => Object.values(qty).reduce((s, n) => s + n, 0), [qty]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!slot) throw new Error("Elige una fecha y un horario.");
      const intlPhone = bookingPhone(contact.phone, phoneCountry);
      if (!intlPhone) throw new Error("Selecciona el país y escribe un número de teléfono válido.");
      const res = await start({
        data: {
          activityId,
          date: slot.date,
          startTimeId: slot.startTimeId,
          rateId: slot.rateId,
          items: slot.prices.map((p) => ({
            pricingCategoryId: p.pricingCategoryId,
            quantity: qty[p.pricingCategoryId] ?? 0,
          })),
          contact: {
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phone: intlPhone,
            language: "es",
          },
          answers: (screen?.product.questions ?? [])
            .filter((q) => q.scope === "booking" && answers[q.id])
            .map((q) => ({ questionId: q.id, values: [answers[q.id] as string] })),
          passengerAnswers: (screen?.product.questions ?? [])
            .filter((q) => q.scope === "passenger" && answers[q.id])
            .map((q) => ({ questionId: q.id, values: [answers[q.id] as string] })),
          extras: Object.entries(extras)
            .filter(([, n]) => n > 0)
            .map(([extraId, quantity]) => ({ extraId, quantity })),
          pickupPlaceId: pickupPlaceId || null,
          pickupNote: pickupNote || null,
          cancellationPolicyVersion: CANCELLATION_POLICY_VERSION,
        },
      });
      return res;
    },
    onSuccess: (res) => {
      if (res.ok) {
        if (res.clientSecret && res.payDeadline)
          setHold({ clientSecret: res.clientSecret, payDeadline: res.payDeadline });
        else window.location.assign(res.url);
      } else {
        setError(res.error);
      }
    },
    onError: (e) => setError((e as Error).message),
  });

  const bookingsPaused = screen && !screen.bookingsEnabled;
  if (hold) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <HoldCheckoutPanel
          clientSecret={hold.clientSecret}
          payDeadline={hold.payDeadline}
          idempotencyKey="(sin-clave-de-intento)"
          onExpired={() => window.location.reload()}
        />
      </main>
    );
  }
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const canSubmit =
    !!slot &&
    participants > 0 &&
    !!contact.firstName.trim() &&
    !!contact.lastName.trim() &&
    /.+@.+\..+/.test(contact.email) &&
    !bookingsPaused &&
    policyAccepted &&
    !mutation.isPending;

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-10 sm:py-14">
      {isLoading && (
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          <p>Consultando disponibilidad…</p>
        </div>
      )}

      {loadError && (
        <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
          {loadError}
        </div>
      )}

      {screen && (
        <div className="space-y-8">
          <header className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {screen.product.title ?? "Reserva tu experiencia"}
            </h1>
            {screen.product.durationMinutes && (
              <p className="text-sm text-muted-foreground">
                Duración aproximada:{" "}
                {screen.product.durationMinutes >= 60
                  ? `${Math.round(screen.product.durationMinutes / 60)} h`
                  : `${screen.product.durationMinutes} min`}
              </p>
            )}
          </header>

          {bookingsPaused && (
            <div
              role="status"
              className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
            >
              <AlertCircle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
              <p>{screen.disabledMessage}</p>
            </div>
          )}

          {/* Fecha y horario */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">Fecha y horario</h2>
            {slots.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Por ahora no hay salidas disponibles. Vuelve a consultar más adelante.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {slots.slice(0, 40).map((s) => {
                  const active = slot?.key === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => {
                        setSlotKey(s.key);
                        setQty({});
                      }}
                      aria-pressed={active}
                      className={`min-h-11 rounded-xl border px-3 py-2 text-sm transition ${
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card text-foreground hover:border-primary/60"
                      }`}
                    >
                      <span className="block font-medium">{s.date}</span>
                      <span className="block text-xs opacity-80">{s.startTime ?? "Horario"}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Viajeros */}
          {slot && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Viajeros</h2>
              {slot.minParticipants && slot.minParticipants > 1 && (
                <p className="text-sm text-muted-foreground">
                  Mínimo {slot.minParticipants} viajeros para esta salida.
                </p>
              )}
              <ul className="space-y-3">
                {slot.prices.map((p) => {
                  const value = qty[p.pricingCategoryId] ?? 0;
                  return (
                    <li
                      key={p.pricingCategoryId}
                      className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{p.title ?? "Viajero"}</p>
                        <p className="text-sm text-muted-foreground">
                          {money(p.amount, slot.currency)} por persona
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={`Quitar ${p.title ?? "viajero"}`}
                          onClick={() =>
                            setQty((q) => ({
                              ...q,
                              [p.pricingCategoryId]: Math.max(0, value - 1),
                            }))
                          }
                        >
                          <Minus className="size-4" aria-hidden="true" />
                        </Button>
                        <span className="w-8 text-center font-medium" aria-live="polite">
                          {value}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={`Agregar ${p.title ?? "viajero"}`}
                          onClick={() =>
                            setQty((q) => ({
                              ...q,
                              [p.pricingCategoryId]: Math.min(20, value + 1),
                            }))
                          }
                        >
                          <Plus className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Extras */}
          {screen.product.extras.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Servicios adicionales</h2>
              <ul className="space-y-2">
                {screen.product.extras.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4"
                  >
                    <span className="min-w-0 text-sm text-foreground">
                      {e.title ?? "Servicio"} {e.required && <em>(obligatorio)</em>}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={`Quitar ${e.title ?? "servicio"}`}
                        onClick={() =>
                          setExtras((x) => ({ ...x, [e.id]: Math.max(0, (x[e.id] ?? 0) - 1) }))
                        }
                      >
                        <Minus className="size-4" aria-hidden="true" />
                      </Button>
                      <span className="w-8 text-center font-medium">{extras[e.id] ?? 0}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={`Agregar ${e.title ?? "servicio"}`}
                        onClick={() =>
                          setExtras((x) => ({ ...x, [e.id]: Math.min(20, (x[e.id] ?? 0) + 1) }))
                        }
                      >
                        <Plus className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Preguntas del producto */}
          {screen.product.questions.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Datos de tu experiencia</h2>
              <div className="space-y-3">
                {screen.product.questions.map((q) => (
                  <div key={`${q.scope}-${q.id}`} className="space-y-1.5">
                    <Label htmlFor={`q-${q.scope}-${q.id}`}>
                      {q.label ?? q.id}
                      {q.required && <span aria-hidden="true"> *</span>}
                    </Label>
                    <Input
                      id={`q-${q.scope}-${q.id}`}
                      value={answers[q.id] ?? ""}
                      onChange={(ev) => setAnswers((a) => ({ ...a, [q.id]: ev.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Recogida */}
          {screen.product.pickupAvailable && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Recogida</h2>
              {screen.product.pickupPlaces.length > 0 && (
                <div className="space-y-1.5">
                  <Label htmlFor="pickup-place">Punto de recogida</Label>
                  <select
                    id="pickup-place"
                    value={pickupPlaceId}
                    onChange={(ev) => setPickupPlaceId(ev.target.value)}
                    className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground"
                  >
                    <option value="">Sin recogida</option>
                    {screen.product.pickupPlaces
                      .filter((p) => p.id)
                      .map((p) => (
                        <option key={p.id} value={p.id as string}>
                          {p.title ?? p.id}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="pickup-note">Hotel o indicaciones (opcional)</Label>
                <Textarea
                  id="pickup-note"
                  value={pickupNote}
                  onChange={(ev) => setPickupNote(ev.target.value)}
                  rows={2}
                />
              </div>
            </section>
          )}

          {/* Contacto */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">Tus datos</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">Nombre</Label>
                <Input
                  id="firstName"
                  autoComplete="given-name"
                  value={contact.firstName}
                  onChange={(ev) => setContact((c) => ({ ...c, firstName: ev.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Apellido</Label>
                <Input
                  id="lastName"
                  autoComplete="family-name"
                  value={contact.lastName}
                  onChange={(ev) => setContact((c) => ({ ...c, lastName: ev.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Correo</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={contact.email}
                  onChange={(ev) => setContact((c) => ({ ...c, email: ev.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Teléfono</Label>
                <BookingPhoneInput invalid={false}
                  country={phoneCountry}
                  value={contact.phone}
                  onCountryChange={setPhoneCountry}
                  onChange={(v) => setContact((c) => ({ ...c, phone: v }))}
                />
              </div>
            </div>
          </section>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
              <p>{error}</p>
            </div>
          )}

          {/* Resumen y pago */}
          <section className="sticky bottom-0 -mx-4 border-t border-border bg-background/95 px-4 py-4 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">
                  {participants} viajero(s)
                  {slot ? ` · ${slot.date}${slot.startTime ? ` ${slot.startTime}` : ""}` : ""}
                </p>
                <p className="text-xl font-semibold text-foreground">
                  {money(total, slot?.currency ?? "MXN")}
                </p>
              </div>
              <div className="w-full rounded-lg border border-border p-3 text-xs">
                <p className="font-semibold">Política de cancelación</p>
                <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                  {cancellationPolicy("es").map((l) => <li key={l}>{l}</li>)}
                </ul>
                <label className="mt-2 flex items-center gap-2 font-semibold">
                  <input type="checkbox" checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} />
                  Acepto la política de cancelación
                </label>
              </div>
              <Button
                type="button"
                size="lg"
                disabled={!canSubmit}
                onClick={() => {
                  setError(null);
                  mutation.mutate();
                }}
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
                    Apartando tu lugar…
                  </>
                ) : (
                  "Apartar y pagar"
                )}
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              El importe final lo calcula el proveedor de la experiencia al apartar tu lugar.
            </p>
          </section>
        </div>
      )}
    </main>
  );
}
